import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { TestCase } from '../types'

export function DocBadge({ label, active }: { label: string; active: boolean }) {
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium border ${
      active
        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/50'
        : 'bg-gray-50 dark:bg-gray-900 text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700'
    }`}>
      {label}
    </span>
  )
}

interface ScenarioFormProps {
  initial?: { name: string; description: string }
  onSave: (name: string, description: string) => Promise<void>
  onCancel: () => void
  saving: boolean
}

export function ScenarioForm({ initial, onSave, onCancel, saving }: ScenarioFormProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    await onSave(name.trim(), description.trim())
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      <div>
        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">{t('scenario_components.name_label')}</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-300"
          placeholder={t('scenario_components.name_placeholder')}
          required
          autoFocus
        />
      </div>
      <div>
        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">{t('scenario_components.description_label')}</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
          rows={2}
          placeholder={t('scenario_components.description_placeholder')}
        />
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-3 py-1.5">
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="bg-blue-600 text-white text-sm font-medium px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? t('common.busy') : t('common.save')}
        </button>
      </div>
    </form>
  )
}

interface TestCaseFormProps {
  initial?: { name: string; description: string }
  existingCase?: TestCase
  filesRequired?: boolean
  onSave: (name: string, description: string, files: File[]) => Promise<void>
  onCancel: () => void
  saving: boolean
}

export function TestCaseForm({ initial, existingCase, filesRequired = true, onSave, onCancel, saving }: TestCaseFormProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [files, setFiles] = useState<File[]>([])
  const fileRef = useRef<HTMLInputElement>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    if (filesRequired && files.length === 0) return
    await onSave(name.trim(), description.trim(), files)
  }

  const isEditing = !!existingCase

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      <div>
        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">{t('scenario_components.name_label')}</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-300"
          placeholder={t('scenario_components.testcase_name_placeholder')}
          required
          autoFocus
        />
      </div>
      <div>
        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">{t('scenario_components.description_label')}</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
          rows={2}
          placeholder={t('scenario_components.description_placeholder')}
        />
      </div>
      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">
          {isEditing ? t('scenario_components.files_replace_label') : t('scenario_components.files_label')}
        </label>
        {isEditing && existingCase && (
          <div className="flex gap-1.5 mb-2">
            <DocBadge label="OR" active={existingCase.has_ordrsp} />
            <DocBadge label="DA" active={existingCase.has_desadv} />
            <DocBadge label="INV" active={existingCase.has_invoice} />
            <span className="text-xs text-gray-400 dark:text-gray-500 self-center ml-1">— {t('scenario_components.current_files_label')}</span>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          multiple
          accept=".xml"
          onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
          className="text-sm text-gray-600 dark:text-gray-400"
        />
        {isEditing && files.length === 0 && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('scenario_components.no_new_files_hint')}</p>
        )}
        {files.length > 0 && (
          <p className="text-xs text-blue-600 mt-1">{t('scenario_components.files_selected_hint', { count: files.length })}</p>
        )}
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-3 py-1.5">
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={saving || !name.trim() || (filesRequired && files.length === 0)}
          className="bg-blue-600 text-white text-sm font-medium px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? t('common.busy') : t('common.save')}
        </button>
      </div>
    </form>
  )
}

interface TestCaseRowProps {
  tc: TestCase
  isAdmin: boolean
  onRun: (tc: TestCase) => Promise<void>
  onDelete: (tc: TestCase) => Promise<void>
  onUpdate: (tc: TestCase, name: string, description: string, files: File[]) => Promise<void>
  running: boolean
}

export function TestCaseRow({ tc, isAdmin, onRun, onDelete, onUpdate, running }: TestCaseRowProps) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)

  const handleUpdate = async (name: string, description: string, files: File[]) => {
    setSaving(true)
    try {
      await onUpdate(tc, name, description, files)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="pl-4 border-l-2 border-gray-100 dark:border-gray-800">
      {editing ? (
        <TestCaseForm
          initial={{ name: tc.name, description: tc.description ?? '' }}
          existingCase={tc}
          filesRequired={false}
          onSave={handleUpdate}
          onCancel={() => setEditing(false)}
          saving={saving}
        />
      ) : (
        <div className="flex items-start justify-between gap-4 py-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{tc.name}</span>
              <DocBadge label="OR" active={tc.has_ordrsp} />
              <DocBadge label="DA" active={tc.has_desadv} />
              <DocBadge label="INV" active={tc.has_invoice} />
              <span className="text-xs font-mono text-gray-300 dark:text-gray-600">#{tc.id}</span>
            </div>
            {tc.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{tc.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => onRun(tc)}
              disabled={running}
              className="inline-flex items-center gap-1 bg-blue-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {running ? (
                <>
                  <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  {t('common.loading')}
                </>
              ) : t('scenario_components.run_button')}
            </button>
            {isAdmin && (
              <>
                <button onClick={() => setEditing(true)} className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                  {t('common.edit')}
                </button>
                <button onClick={() => onDelete(tc)} className="text-xs text-red-400 hover:text-red-600">
                  {t('common.delete')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
